import { RuleQueryResult } from '@/graphql/generated/hooks'
import {
  AiOperatorFieldsQuery,
  DefaultAiOperatorModelQuery,
  DevicesWithTailgateConfiguredQuery,
  EnabledAiOperatorModelsQuery,
} from '@/graphql/generated/operations'
import {
  AiOperatorActionType,
  AiOperatorField,
  AiOperatorModel,
  RuleEventType,
} from '@/graphql/generated/schemas'
import { uuid } from '@/utils/helpers'

import {
  AiOperatorModelT,
  Criterion,
  CriterionOption,
  DeviceConfigurationT,
} from '../types/types'

export const AiOperatorCriterionMapper: Record<AiOperatorActionType, string> = {
  RESOLVE_AND_MARK_AS_FALSE: 'Resolve',
  FLAG_AND_RELEASE: 'Flag',
}

const NO_UNAUTHORIZED_ENTRY = 'No Unauthorized Entry'
const DOOR_LEFT_OPEN = 'Door Left Open'
const PRIOR_ACCESS_GRANTED = 'Prior Access Granted'
const DOOR_CLOSING = 'Door Closing'
export const DEFAULT_MODEL_ID = 'default'

const DFO_CRITERIA = [NO_UNAUTHORIZED_ENTRY, PRIOR_ACCESS_GRANTED]
const DHO_DOA_CRITERIA = [DOOR_LEFT_OPEN, DOOR_CLOSING]
const TRACE_LOGS_GATED_CRITERIA = new Set<string>([
  PRIOR_ACCESS_GRANTED,
  DOOR_CLOSING,
])

export const AI_OPERATOR_DOOR_EVENT_TYPES = new Set<RuleEventType>([
  RuleEventType.DoorForcedOpen,
  RuleEventType.DoorHeldOpen,
  RuleEventType.DoorOpenAlarm,
])

const isAllowedCriterion = (
  criterion: string,
  isTraceLogsEnabled: boolean
): boolean => {
  const isGatedByTraceLogs = TRACE_LOGS_GATED_CRITERIA.has(criterion)
  return !isGatedByTraceLogs || isTraceLogsEnabled
}

/**
 * Get the default action based on the criterion name
 * @param name - Criterion name
 * @returns Default action type
 */
export const getDefaultActionForCriterion = (
  desc: string
): AiOperatorActionType => {
  return desc.toLocaleLowerCase() === DOOR_LEFT_OPEN.toLocaleLowerCase()
    ? AiOperatorActionType.FlagAndRelease
    : AiOperatorActionType.ResolveAndMarkAsFalse
}

type AiOperatorResponseRulesFromQuery = NonNullable<
  RuleQueryResult['data']
>['rule']['aiOperatorResponseRules']

/**
 * Converts AI Operator fields query data to criterion options for dropdowns
 */
export const getAiOperatorFieldOptions = (
  data: AiOperatorFieldsQuery,
  selectedEventTypes: RuleEventType[],
  isTraceLogsEnabled: boolean
): Criterion[] => {
  const allowedCriteriaDescriptions = new Set<string>()

  if (selectedEventTypes.includes(RuleEventType.DoorForcedOpen)) {
    DFO_CRITERIA.forEach((criterion) => {
      if (isAllowedCriterion(criterion, isTraceLogsEnabled)) {
        allowedCriteriaDescriptions.add(criterion)
      }
    })
  }

  if (
    selectedEventTypes.includes(RuleEventType.DoorHeldOpen) ||
    selectedEventTypes.includes(RuleEventType.DoorOpenAlarm)
  ) {
    DHO_DOA_CRITERIA.forEach((criterion) => {
      if (isAllowedCriterion(criterion, isTraceLogsEnabled)) {
        allowedCriteriaDescriptions.add(criterion)
      }
    })
  }

  const modifiedData: Criterion[] = data?.aiOperatorFields?.edges?.map((f) => ({
    id: f?.node?.id,
    action: getDefaultActionForCriterion(f?.node?.description),
    criterion: {
      id: f?.node?.id,
      description: f?.node?.description,
    },
    detected: true,
  }))
  return (
    modifiedData?.filter((criterion) =>
      allowedCriteriaDescriptions.has(criterion?.criterion?.description)
    ) ?? []
  )
}

/**
 * Returns all devices which are missing device configurations. Either devices are not associated or missing a tailgate grid
 *
 * @param devicesData - Device DevicesWithTailgateConfiguredQuery data
 * @returns All misconfiguared door devices
 */
export const getDeviceConfigurations = (
  devicesData: DevicesWithTailgateConfiguredQuery
): DeviceConfigurationT[] => {
  return (
    devicesData?.devices?.edges?.flatMap((edge) => {
      const door = edge?.node?.door
      const isAssociated = door?.tailgateGrids?.length > 0
      const isConfigured = door?.tailgateGrids?.some(
        (grid) => grid?.inBox?.topLeft
      )
      if (!isAssociated || !isConfigured) {
        return {
          id: edge.node.id,
          name: edge.node.name,
          floorId: edge.node.floor?.id,
          isAssociated,
          isConfigured,
        }
      }
      return []
    }) || []
  )
}

export const containsNoUnauthorizedEntry = (criteria: Criterion[]): boolean => {
  return criteria?.some(
    (crit) =>
      crit.criterion?.description?.toLowerCase() ===
      NO_UNAUTHORIZED_ENTRY.toLocaleLowerCase()
  )
}
/**
 * Returns all enabled AI operator models from the query data
 *
 * @param aiOperatorModelsData - EnabledAiOperatorModelsQuery data
 * @returns Array of AI operator models
 */
export const getAiOperatorModels = (
  aiOperatorModelsData?: EnabledAiOperatorModelsQuery
): AiOperatorModelT[] => {
  return (
    aiOperatorModelsData?.enabledAiOperatorModels?.edges
      ?.map((edge) => edge?.node)
      ?.filter(Boolean)
      ?.map((model) => ({
        id: model.id,
        canonicalName: model.canonicalName,
        provider: model.provider,
      })) || []
  )
}

/**
 * Returns the default AI operator model from the query data
 *
 * @param defaultAiOperatorModelData - DefaultAiOperatorModelQuery data
 * @returns Default AI operator model or null
 */
export const getDefaultAiOperatorModel = (
  defaultAiOperatorModelData?: DefaultAiOperatorModelQuery
): AiOperatorModelT | null => {
  const model =
    defaultAiOperatorModelData?.defaultAiOperatorModel?.aiOperatorModel
  if (!model) return null

  return {
    id: model.id,
    canonicalName: model.canonicalName,
    provider: model.provider,
  }
}

const defaultModel = {
  id: DEFAULT_MODEL_ID,
  canonicalName: 'Default',
  provider: 'DEFAULT',
}

/**
 * Gets the models to show in a criterion's dropdown based on the viewing mode
 *
 * @param criterion - The criterion object containing model information
 * @param allModels - All available AI operator models (from query)
 * @param isViewingMode - Whether we're viewing an existing rule (disabled mode)
 * @returns Array of models to display in the dropdown
 *
 * Logic:
 * - Creation mode: Show "Default" option plus all available models
 * - Viewing mode + explicit model: Show only that specific model
 * - Viewing mode + no explicit model: Show "Default" label
 */
export const getModelsForCriterionDropdown = (
  criterion: {
    aiOperatorModelId?: string
    aiOperatorModel?: AiOperatorModelT
  },
  allModels: AiOperatorModelT[],
  isViewingMode = false
): Array<{ id: string; canonicalName: string; provider: string }> => {
  // Creation mode: show "Default" option plus all available models
  if (!isViewingMode) {
    return [defaultModel, ...allModels]
  }

  if (criterion.aiOperatorModel) {
    return [criterion.aiOperatorModel]
  }

  // No explicit model = using default, show "Default" label
  if (!criterion.aiOperatorModelId) {
    return [defaultModel]
  }

  return []
}

/**
 * Calculates available criteria options for a specific item, excluding options
 * already used by other criteria items. This prevents duplicate selections
 * in the dropdowns.
 *
 * @param currentItem - The criterion item we're calculating options for
 * @param allCriteria - All criteria currently in the list
 * @param availableCriteria - All available criteria from the API
 * @returns Filtered array of criteria options available for this specific item
 */
export const getAvailableCriteriaForItem = (
  currentItem: Criterion,
  allCriteria: Criterion[],
  availableCriteria: Criterion[]
): CriterionOption[] => {
  const usedCriteriaValues = allCriteria
    .filter((c) => c.id !== currentItem.id)
    .map((c) => c.criterion.id)

  return availableCriteria
    .map((c) => c.criterion)
    .filter((option) => !usedCriteriaValues.includes(option.id))
}

/**
 * Converts GraphQL AiOperatorField to CriterionOption
 */
export const convertFieldToCriterionOption = (
  field: AiOperatorField
): CriterionOption => ({
  id: field.id,
  description: field.description ?? '',
})

/**
 * Converts GraphQL AiOperatorModel to AiOperatorModelT
 * Accepts any object with id, canonicalName, and provider fields
 */
export const convertModelToModelT = (model: {
  id: string
  canonicalName: string
  provider: AiOperatorModel['provider']
}): AiOperatorModelT => ({
  id: model.id,
  canonicalName: model.canonicalName,
  provider: model.provider,
})

/**
 * Converts GraphQL AiOperatorResponseRule to Criterion with proper type safety
 */
export const convertResponseRuleToCriterion = (
  rule: AiOperatorResponseRulesFromQuery[number]
): Criterion => {
  return {
    id: uuid(),
    action: rule.action,
    criterion: convertFieldToCriterionOption(rule.field),
    detected: true,
    aiOperatorModelId: rule.model?.id,
    aiOperatorModel: rule.model ? convertModelToModelT(rule.model) : undefined,
  }
}

/**
 * Sorts response rules by priority (highest first) with null safety
 */
export const sortResponseRulesByPriority = (
  rules: AiOperatorResponseRulesFromQuery
): AiOperatorResponseRulesFromQuery => {
  return [...rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
}

/**
 * Converts array of GraphQL AiOperatorResponseRule to Criterion array for preview/view mode
 */
export const convertResponseRulesToPreviewData = (
  responseRules: AiOperatorResponseRulesFromQuery
): Criterion[] => {
  if (responseRules.length === 0) {
    return []
  }

  const sortedRules = sortResponseRulesByPriority(responseRules)
  return sortedRules.map(convertResponseRuleToCriterion)
}
